import { nanoid } from 'nanoid';

import { ColorfulGameBG } from '../../constants/Games/gameBackground';
import { Icons, IconType } from '../../constants/icons';
import { DefaultColors } from '../../constants/Theme/DefaultColors';
import { BalanceJson, PartKey } from '../../models/Base';
import {
  ExtractDisplayDataFn,
  FetchDataFn,
  FileUrls,
  FinalizeRecordFn,
  GameControllerResult,
  GameInfo,
  InitializeRecordFn,
  RefineResultFn,
} from '../../models/GameControllerStore';
import { GAME_THEME_COLOR } from '../../models/GameControllerStore/theme';
import { GameReply } from '../../models/GameStore';
import {
  ChallengeRecord,
  ChallengeRecordJson,
  ChallengeRecordMode,
  ChallengeRecordType,
  GameRecordJson,
  GameRecordType,
} from '../../models/RecordStore';
import { AlbumStatisticsKey } from '../../models/SpaceStore';
import { getFromI18n } from '../../utils/i18n';
import { customLog } from '../../utils/logger';
import { areFloatsEqual } from '../../utils/math';
import { computeReward, getStarsByScore } from '../../utils/record';
import { getContentLocale, getSpokenLocale } from '../../utils/space';
import { useFileStore } from '../FileStore';
import { useRecordStore } from '../RecordStore';
import { useSpaceStore } from '../SpaceStore';
import { useUserStore } from '../UserStore';

import { fetchChallengeRecords, fetchGames } from './fetch-data';
import {
  ChallengeIntermediate,
  ChallengeParams,
  prepareChallengeIntermediate,
} from './prepare-intermediate';
import {
  applyLocaleAndVariant,
  combinePairingGames,
  // computeRevisionSchedule,
  computeScore,
  resolveAutoTimeLimit,
} from './refine-result';

export type GameControllerSimpleChallengeParams = ChallengeParams;

type ResultRecords = {
  challengeRecords: ChallengeRecord[];
  // revisionSchedule: RevisionSchedule | null;
};

export type GameControllerSimpleChallengeResult =
  GameControllerResult<ResultRecords> & { activeRecord?: ChallengeRecord };

export const getChallengeData = (
  challengeIndex: number | 'super' | 'comprehensive',
): {
  type: ChallengeRecordType;
  title: string;
  icon: IconType;
  reward: BalanceJson;
  playCountKey?: AlbumStatisticsKey;
  scoreTotalKey?: AlbumStatisticsKey;
} => {
  if (challengeIndex === 'comprehensive') {
    return {
      type: 'comprehensiveChallenge',
      title: 'Challenge All',
      icon: IconType.SuperTargetWhite,
      reward: { breadSlice: 50 },
      playCountKey: undefined,
      scoreTotalKey: undefined,
    };
  } else if (challengeIndex === 'super') {
    return {
      type: 'superChallenge',
      title: 'Super Challenge',
      icon: IconType.SuperTargetWhite,
      reward: { breadSlice: 50 },
      playCountKey: 'superChallengePlayCount',
      scoreTotalKey: 'superChallengeScoreTotal',
    };
  } else {
    const partId = String(challengeIndex).padStart(2, '0') as PartKey;
    return {
      type: `pathChallenge_${partId}` as ChallengeRecordType,
      title: 'Mini Challenge',
      icon: IconType.MiniTargetWhite,
      reward: { breadSlice: 30 },
      playCountKey: `pathChallenge_${partId}PlayCount`,
      scoreTotalKey: `pathChallenge_${partId}ScoreTotal`,
    };
  }
};
const getBodyColor = (theme: number | undefined): string => {
  return (
    GAME_THEME_COLOR[(theme ?? 0) % GAME_THEME_COLOR.length] ??
    GAME_THEME_COLOR[0]
  ).backgroundColor;
};
const getBodyArrowColor = (theme: number | undefined): string => {
  return (
    GAME_THEME_COLOR[(theme ?? 0) % GAME_THEME_COLOR.length] ??
    GAME_THEME_COLOR[0]
  ).arrow;
};
const extractDisplayData: ExtractDisplayDataFn<
  GameControllerSimpleChallengeParams,
  ChallengeIntermediate,
  GameControllerSimpleChallengeResult
> = (
  { t, locale, userId },
  { classroomId, gameSetId, challengeIndex },
  intermediate,
  result,
) => {
  const { getSpace, getMembership, getAlbum } = useSpaceStore.getState();
  const { getFileDownload } = useFileStore.getState();

  const space = getSpace(classroomId);
  const membership = getMembership(classroomId, userId);
  const album = getAlbum(gameSetId);
  const { title, reward, icon, playCountKey, scoreTotalKey } =
    getChallengeData(challengeIndex);

  // const partSummary = intermediate
  //   ? getAlbumSummary(gameSetId)?.parts[intermediate.partIndex]
  //   : undefined;
  const game = result?.gameSequence?.[result.defaultIndex];
  const lastestChallengeRecord = result?.records?.challengeRecords.filter(
    (record) => record.completed,
  )?.[0];
  // const isFirstTimePlay = (result?.records?.challengeRecords.length ?? 0) === 0;
  const variant = game?.gameData;
  const spaceIcon = space?.icon
    ? getFileDownload(space.icon)?.getVersion()?.uri
    : undefined;
  const { reward: maximumReward, memberReward } = computeReward(
    membership?.isAtLeastMember,
    reward,
    false,
    5,
    result?.records?.challengeRecords.map((record) => record.reward) ?? [],
    1,
  );
  const playCount = playCountKey ? album?.statistics[playCountKey] ?? 0 : 0;
  const scoreTotal = scoreTotalKey ? album?.statistics[scoreTotalKey] ?? 0 : 0;

  // const gameBackgrounds =
  //   album?.content?.gameDesign.backgrounds.education.concat(
  //     album.content.gameDesign.backgrounds.enterprise,
  //   );

  // const gameBGIndex = gameBackgrounds
  //   ? gameBackgrounds[Math.floor(gameBackgrounds.length * Math.random())]?.set
  //   : undefined;

  const gameBGIndex = Math.floor(ColorfulGameBG.length * Math.random());

  return {
    classroomId,
    gameSetId,
    theme: 'challenge',
    isChallenge: true,
    oneTimeFee: true,
    gameBGIndex: gameBGIndex,
    context: {
      icon: spaceIcon ? spaceIcon : Icons[IconType.Classroom],
      title: getFromI18n(space?.i18nClassroomInfo, locale)?.name ?? '',
      color: DefaultColors.green.OPPONENT_GREEN,
    },
    progress: {
      icon: Icons[IconType.Game],
      title: title,
      completed: 0, // partSummary?.completedGameCount ?? 0,
      total: 0,
    },
    body: {
      index: undefined,
      icon: icon,
      title: t(title),
      color: getBodyColor(0),
      arrowColor: getBodyArrowColor(0),
      gameIconTag: undefined,
    },
    latestRecord: lastestChallengeRecord
      ? {
          score: lastestChallengeRecord.score,
          durationInMs: lastestChallengeRecord.durationInMs,
        }
      : undefined,
    completedBefore:
      result?.records?.challengeRecords?.some((r) => r.completed) ?? false,
    maximumReward,
    maximumShowReward: undefined,
    fee: { token: 1 },
    statistics: album?.statistics
      ? {
          playCount: playCount,
          averageScore: playCount ? scoreTotal / playCount : 0,
        }
      : undefined,
    partIndex: undefined,
    memberReward: memberReward ?? undefined,
    customReplies:
      variant !== undefined && 'replies' in variant
        ? (variant.replies as GameReply[])
        : undefined,
    vsMode: false,
    dummies: undefined,
  };
};

type GameControllerSimpleChallengeData = {
  games: GameInfo[];
  challengeRecords: ChallengeRecord[];
  // revisionSchedule: RevisionSchedule | null;
  // opponents: GameOpponentsData & { profiles: { profile: ProfileJson }[] };
};

type RecordJson = {
  gameRecord: GameRecordJson;
  challengeRecord: ChallengeRecordJson;
};

const fetchData: FetchDataFn<
  GameControllerSimpleChallengeParams,
  GameControllerSimpleChallengeData,
  ChallengeIntermediate
> = async (
  _utils,
  { classroomId, gameSetId, challengeIndex },
  { gameItems },
) => {
  const { type } = getChallengeData(challengeIndex);

  const games = await fetchGames({ classroomId, gameSetId }, gameItems);
  const gameProgress =
    gameItems.length > 0
      ? games.filter((game) => game !== undefined).length / gameItems.length
      : 0;

  const challengeRecords = await fetchChallengeRecords(
    { classroomId, gameSetId },
    type,
  );
  const challengeRecordProgress = challengeRecords === undefined ? 0 : 1;

  // const revisionSchedule = await fetchRevisionSchedule({ ...params, userId });
  // const revisionScheduleProgress = revisionSchedule === undefined ? 0 : 1;

  const progress = gameProgress * 0.6 + challengeRecordProgress * 0.4;
  // + revisionScheduleProgress * 0.2;
  if (
    areFloatsEqual(progress, 1) &&
    challengeRecords !== undefined
    // && revisionSchedule !== undefined
  ) {
    // const opponents = await fetchGameOpponents(params);
    // console.log(opponents);
    // await fetchPuzzles(params, userId, 1, gameRecords);

    return {
      result: {
        games: games as GameInfo[],
        challengeRecords,
        // revisionSchedule,
        // opponents,
      },
      progress: 1,
    };
  }
  return { progress };
};

const refineResult: RefineResultFn<
  GameControllerSimpleChallengeParams,
  GameControllerSimpleChallengeData,
  ChallengeIntermediate,
  GameControllerSimpleChallengeResult
> = (
  { userId },
  {
    classroomId,
    // gameSetId,
    // challengeIndex,
  },
  intermediate, //{ gameItems },
  {
    games,
    challengeRecords,
    // revisionSchedule,
    // opponents,
  },
) => {
  const { getSpace, getMembership } = useSpaceStore.getState();

  const classroom = getSpace(classroomId);
  const membership = getMembership(classroomId, userId);
  const contentLocale = getContentLocale(classroom, membership);
  const soundLocale = getSpokenLocale(classroom, membership);

  const gameSequence = combinePairingGames(
    games.map((g) => applyLocaleAndVariant(g, contentLocale, soundLocale)),
    true,
  ).map(resolveAutoTimeLimit);

  return {
    gameSequence,
    defaultIndex: 0,
    fileUrls: games.reduce(
      (acc, cur) => {
        cur.dependencies.images.forEach((id) => {
          acc.images[id] = undefined;
        });
        cur.dependencies.audios.forEach((id) => {
          acc.audios[id] = undefined;
        });
        cur.dependencies.videos.forEach((id) => {
          acc.videos[id] = undefined;
        });
        cur.dependencies.resourceImages.forEach((id) => {
          acc.resourceImages[id] = undefined;
        });
        cur.dependencies.strokes.forEach((id) => {
          acc.strokes[id] = undefined;
        });
        cur.dependencies.ttsAudios.forEach((id) => {
          acc.ttsAudios[id] = undefined;
        });
        return acc;
      },
      {
        images: {},
        audios: {},
        videos: {},
        resourceImages: {},
        strokes: {},
        ttsAudios: {},
      } as FileUrls,
    ),
    records: {
      challengeRecords,
      //revisionSchedule
    },
    activeRecord: undefined,
  };
};

export const initializeRecord: InitializeRecordFn<
  GameControllerSimpleChallengeParams,
  ChallengeIntermediate,
  GameControllerSimpleChallengeResult,
  RecordJson
> = async (
  { userId },
  { classroomId, gameSetId, challengeIndex },
  _,
  { gameSequence, defaultIndex },
  { fee, record, index },
) => {
  const { getProfile } = useUserStore.getState();
  const { type } = getChallengeData(challengeIndex);
  const userProfile = getProfile(userId);

  const i = index ?? defaultIndex;
  const now = Date.now();
  const game = gameSequence[i];
  if (game === undefined) {
    throw new Error('Invalid game');
  }

  let challengeRecord: ChallengeRecordJson;
  if (record) {
    challengeRecord = record.challengeRecord;
  } else {
    challengeRecord = {
      challengeRecordId: nanoid(),
      classroomId,
      gameSetId,
      type,
      playedBy: userId,
      createdAt: now,
      startedAt: now,
      mode: ChallengeRecordMode.Simple,
      isFirstTime: false,
      games: gameSequence.filter(Boolean).map((item) => ({
        gameCollectionId: item!.gameCollectionId,
        gameId: item!.gameId,
        gameHash: item!.hash,
        reward: {},
        opponent: null,
      })),
    };
    customLog(challengeRecord, userProfile);
    challengeRecord = await useRecordStore
      .getState()
      .createChallengeRecord(challengeRecord);
  }

  let gameRecord: GameRecordJson = {
    gameRecordId: nanoid(),
    createdAt: now,
    classroomId,
    gameSetId,
    gameCollectionId: game.gameCollectionId,
    gameId: game.gameId,
    gameHash: game.hash,
    challengeRecordId: challengeRecord.challengeRecordId,
    type: GameRecordType.Challenge,
    playedBy: userId,
    sessionStartedAt: now,
    startedAt: now,
    fee,
  };
  const result = await useRecordStore
    .getState()
    .syncGameRecord({ ...gameRecord, index: i });
  gameRecord = result.gameRecord;
  if (result.challengeRecord) {
    challengeRecord = result.challengeRecord;
  }

  return { gameRecord, challengeRecord };
};

export const finalizeRecord: FinalizeRecordFn<
  GameControllerSimpleChallengeParams,
  ChallengeIntermediate,
  GameControllerSimpleChallengeResult,
  RecordJson
> = async (
  { userId },
  { classroomId, gameSetId },
  _,
  { gameSequence, records },
  { record, scores, durationInMs, inAppEvaluations, audios, submitAudio },
) => {
  const { getProfile } = useUserStore.getState();
  const userProfile = getProfile(userId);

  customLog('finalize record', userProfile);
  customLog(
    {
      record,
      scores,
      durationInMs,
      inAppEvaluations,
      audios,
      submitAudio,
    },
    userProfile,
  );

  // const {
  //   // getMembership,
  //   // incrementMembershipFields
  // } = useSpaceStore.getState();
  const {
    // incrementAlbumSummaryPart,
    syncGameRecord,
    clearPlayingRecord,
    updateAlbumSummary,
  } = useRecordStore.getState();

  const index = gameSequence.findIndex(
    (g) => g?.gameId === record.gameRecord.gameId,
  );
  if (index === -1) {
    throw new Error('Invalid game');
  }

  const game = gameSequence[index]!;

  clearPlayingRecord(); // FIXME: no more playing records because await when init

  // const membership = getMembership(classroomId, userId);
  const score = computeScore(scores.undefined?.[0] ?? 0, 1);

  const firstTimeCompleted = records.challengeRecords.every(
    (r) => !r.completed,
  );
  if (firstTimeCompleted) {
    // incrementAlbumSummaryPart(gameSetId, partIndex, { completedGameCount: 1 });
  }

  // let newGameRecord: GameRecordJson;
  const needToUpdateAlbumSummary = index === gameSequence.length - 1;

  let newGameRecord: GameRecordJson = {
    ...record.gameRecord,
    endedAt: Date.now(),
    durationInMs,
    reward: {},
    score,
    stars: getStarsByScore(score),
    defeatedOpponents: null,
    evaluation: inAppEvaluations[0]?.word
      ? { gameType: game.gameType, inApp: inAppEvaluations[0].word }
      : undefined,
  };

  // incrementMembershipFields(classroomId, userId, {
  //   balance: { classroomCredit: newRecord.reward?.classroomCredit },
  //   puzzle: { collectedPieceCount: newRecord.reward?.puzzlePiece },
  // });

  // const revisionSchedule = computeRevisionSchedule({
  //   classroomId,
  //   gameSetId,
  //   gameId,
  //   gameCollectionId,
  //   userId,
  //   revisionSchedule: records.revisionSchedule,
  //   newRecord: {
  //     playedBy: record.playedBy,
  //     gameHash: record.gameHash,
  //     startedAt: record.startedAt ?? Date.now(),
  //     endedAt: record.endedAt ?? Date.now(),
  //     score,
  //     durationInMs,
  //     fee: record.fee,
  //     reward,
  //   },
  // });

  // sync data
  const result = await syncGameRecord({ ...newGameRecord, index });
  newGameRecord = result.gameRecord;
  const challengeRecord = result.challengeRecord ?? record.challengeRecord;

  if (needToUpdateAlbumSummary) {
    await new Promise((resolve) => setTimeout(resolve, 1000));
    await updateAlbumSummary(classroomId, gameSetId);
  }
  customLog(newGameRecord, userProfile);
  // await syncRevisionSchedule(revisionSchedule.toJSON());

  return {
    record: {
      challengeRecord,
      gameRecord: newGameRecord,
    },
  };
};

export const preprocess = {
  extractDisplayData,
  prepareIntermediate: prepareChallengeIntermediate,
  fetchData,
  refineResult,
  initializeRecord,
  finalizeRecord,
};
